import React, { useEffect, useState, useRef } from 'react';

// Helper function to get a female voice
const getFemaleVoice = () => {
  if (!('speechSynthesis' in window)) return null;
  
  const voices = window.speechSynthesis.getVoices();
  
  // Common female voice name patterns
  const femaleVoicePatterns = [
    /female/i,
    /woman/i,
    /zira/i,        // Microsoft Zira (Windows)
    /samantha/i,    // Apple Samantha (macOS)
    /victoria/i,    // Apple Victoria (macOS)
    /karen/i,       // Australian English Female
    /fiona/i,       // Scottish English Female
    /tessa/i,       // South African English Female
    /veena/i,       // Indian English Female
    /google.*female/i,
    /microsoft.*female/i,
  ];
  
  // Try to find a female voice
  for (const pattern of femaleVoicePatterns) {
    const femaleVoice = voices.find(voice => 
      pattern.test(voice.name) && voice.lang.startsWith('en')
    );
    if (femaleVoice) {
      console.log('Selected female voice:', femaleVoice.name);
      return femaleVoice;
    }
  }
  
  // Fallback: Look for any English voice that's not explicitly male
  const malePatterns = [/male/i, /david/i, /alex/i, /daniel/i, /thomas/i];
  const englishVoices = voices.filter(voice => 
    voice.lang.startsWith('en') && 
    !malePatterns.some(pattern => pattern.test(voice.name))
  );
  
  if (englishVoices.length > 0) {
    // Prefer voices that might be female (higher index often means female in some systems)
    const selectedVoice = englishVoices[englishVoices.length > 1 ? 1 : 0];
    console.log('Selected voice (fallback):', selectedVoice.name);
    return selectedVoice;
  }
  
  // Last resort: return first English voice
  const firstEnglish = voices.find(voice => voice.lang.startsWith('en'));
  if (firstEnglish) {
    console.log('Selected default English voice:', firstEnglish.name);
    return firstEnglish;
  }
  
  return null;
};

export default function VoiceOutput({ text, onSpeakingComplete, autoPlay = true }) {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [subtitles, setSubtitles] = useState('');
  const utteranceRef = useRef(null);
  const [femaleVoice, setFemaleVoice] = useState(null);
  const isProcessingRef = useRef(false); // Prevent multiple simultaneous speech requests
  const [hasSpokenGreeting, setHasSpokenGreeting] = useState(false); // Track if initial greeting has been spoken
  const spokenTextsRef = useRef(new Set()); // Track all texts that have been spoken
  
  // Load voices when component mounts
  useEffect(() => {
    const loadVoices = () => {
      const voice = getFemaleVoice();
      setFemaleVoice(voice);
    };
    
    // Voices might not be loaded immediately
    if (window.speechSynthesis.getVoices().length > 0) {
      loadVoices();
    } else {
      // Wait for voices to be loaded
      window.speechSynthesis.onvoiceschanged = loadVoices;
      // Also try after a short delay
      setTimeout(loadVoices, 100);
    }
    
    return () => {
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, []);

  // Track last spoken text to prevent duplicate speech
  const textChangeTimeoutRef = useRef(null);
  const speechTimeoutRef = useRef(null);
  
  useEffect(() => {
    if (!text || !autoPlay) return;

    // Check browser support
    if (!('speechSynthesis' in window)) {
      console.warn('Speech synthesis not supported');
      setSubtitles(text);
      return;
    }

    // Normalize text for comparison (trim and lowercase)
    const normalizedText = text.trim().toLowerCase();
    
    // Check if we've already spoken this exact text
    if (spokenTextsRef.current.has(normalizedText)) {
      console.log('🔇 Skipping duplicate speech request - text already spoken:', text.substring(0, 50));
      setSubtitles(text); // Still show subtitles
      return;
    }

    // For initial greeting: only speak once
    // Check if this looks like a greeting (contains common greeting words)
    const isGreeting = (
      normalizedText.includes('hello') ||
      normalizedText.includes('hi') ||
      normalizedText.includes('welcome') ||
      normalizedText.includes('connected') ||
      normalizedText.includes("i'm aura") ||
      normalizedText.includes("i am aura")
    );
    
    // If this is a greeting and we've already spoken a greeting, skip it
    if (isGreeting && hasSpokenGreeting) {
      console.log('🔇 Initial greeting already spoken, skipping duplicate greeting');
      setSubtitles(text);
      return;
    }

    // Clear any pending timeout
    if (textChangeTimeoutRef.current) {
      clearTimeout(textChangeTimeoutRef.current);
    }

    // Debounce: Wait a bit to see if text changes again (prevents rapid re-speech)
    textChangeTimeoutRef.current = setTimeout(() => {
      // Double-check we haven't spoken this text while waiting
      if (spokenTextsRef.current.has(normalizedText)) {
        console.log('🔇 Text was spoken while debouncing, skipping');
        setSubtitles(text);
        return;
      }

      // Prevent multiple simultaneous speech requests
      if (isProcessingRef.current) {
        console.warn('Speech synthesis already processing, skipping duplicate request');
        setSubtitles(text); // Still show subtitles
        return;
      }

      // Check if speech synthesis is actually available (not just the API)
      if (window.speechSynthesis.speaking) {
        console.warn('Speech synthesis is already speaking, cancelling and starting new...');
        // Cancel current speech and start new one
        window.speechSynthesis.cancel();
        // Wait a bit for cancellation to complete, then retry
        setTimeout(() => {
          if (!window.speechSynthesis.speaking && !isProcessingRef.current) {
            // Retry by triggering a re-render
            setSubtitles(text);
          }
        }, 500);
        return; // Exit early - will retry after cancellation
      }

      // Mark as processing
      isProcessingRef.current = true;
      
      // Mark this text as about to be spoken (prevent race conditions)
      spokenTextsRef.current.add(normalizedText);

      // Cancel any ongoing speech before starting new one
      window.speechSynthesis.cancel();
    
      // Clear any existing speech timeout
      if (speechTimeoutRef.current) {
        clearTimeout(speechTimeoutRef.current);
      }
    
      // Longer delay to ensure cancellation completes and queue clears
      // This prevents 'interrupted' errors from rapid cancellations
      speechTimeoutRef.current = setTimeout(() => {
      // Ensure we have the full text (not truncated)
      const fullText = typeof text === 'string' ? text : String(text || '');
      
      if (!fullText || fullText.trim() === '') {
        return;
      }
      
      console.log('Speaking text length:', fullText.length, 'First 50 chars:', fullText.substring(0, 50));
      
      // Check if speech synthesis is still available (not cancelled by browser)
      if (window.speechSynthesis.speaking) {
        console.warn('Speech synthesis still speaking, waiting longer...');
        // Wait a bit more
        setTimeout(() => {
          if (!window.speechSynthesis.speaking && !isProcessingRef.current) {
            // Retry by showing subtitles
            setSubtitles(fullText);
          }
        }, 500);
        return;
      }
      
      // Get female voice (re-fetch in case voices weren't loaded initially)
      const voice = femaleVoice || getFemaleVoice();
      
      // For very long text (>1000 chars), consider splitting or truncating
      // Some browsers have issues with very long utterances
      let textToSpeak = fullText;
      if (fullText.length > 1000) {
        console.warn(`Text is very long (${fullText.length} chars). Truncating to first 1000 chars to prevent interruption.`);
        // Truncate but keep it meaningful
        textToSpeak = fullText.substring(0, 1000) + '...';
        // Still show full text in subtitles
        setSubtitles(fullText);
      }
      
      // Create new utterance with text
      const utterance = new SpeechSynthesisUtterance(textToSpeak);
      utterance.lang = 'en-US';
      utterance.rate = 1.0;
      utterance.pitch = 1.1; // Slightly higher pitch for more feminine sound
      utterance.volume = 1.0;
      
      // Set female voice if available
      if (voice) {
        utterance.voice = voice;
        console.log('Using voice:', voice.name);
      } else {
        console.warn('No female voice found, using default');
      }

      // Capture isGreeting and normalizedText for use in event handlers
      const currentIsGreeting = isGreeting;
      const currentNormalizedText = normalizedText;
      
      // Event handlers
      utterance.onstart = () => {
        setIsSpeaking(true);
        setSubtitles(fullText); // Always show full text in subtitles
        isProcessingRef.current = false; // Reset processing flag when speech starts
        
        // 🟢 Notify the rest of the app that TTS has started (for VoiceInput.jsx to mute mic)
        window.dispatchEvent(new CustomEvent('aura-tts-start', { 
          detail: { text: fullText.substring(0, 50) } // Include text for debugging
        }));
        
        // Mark greeting as spoken if this is the initial greeting
        if (currentIsGreeting && !hasSpokenGreeting) {
          setHasSpokenGreeting(true);
          console.log('✅ Initial greeting speech started');
        }
        
        console.log('✅ Speech synthesis started successfully');
      };

      utterance.onend = () => {
        setIsSpeaking(false);
        setSubtitles(''); // Clear subtitles when speech ends
        isProcessingRef.current = false; // Reset processing flag
        
        // 🟢 Notify the rest of the app that TTS has ended (for VoiceInput.jsx to unmute mic)
        window.dispatchEvent(new CustomEvent('aura-tts-end'));
        
        // Mark greeting as spoken after speech completes
        if (currentIsGreeting && !hasSpokenGreeting) {
          setHasSpokenGreeting(true);
        }
        
        if (onSpeakingComplete) {
          onSpeakingComplete();
        }
      };

      utterance.onerror = (error) => {
        // Log detailed error information
        const errorType = error.error || 'unknown';
        const errorMessage = error.message || 'Speech synthesis failed';
        console.error('Speech synthesis error:', {
          type: errorType,
          message: errorMessage,
          charIndex: error.charIndex,
          utterance: error.utterance?.text?.substring(0, 50) || 'N/A'
        });
        
        // Handle different error types
        switch (errorType) {
          case 'interrupted':
            // Speech was cancelled before it could start - this is often harmless
            // It can happen if:
            // 1. A new speech request cancels the previous one
            // 2. Text is too long and browser cancels it
            // 3. User interaction cancels it
            console.warn('Speech synthesis interrupted - this may be normal if a new request started');
            // Don't treat this as a critical error - just show subtitles
            break;
          case 'network':
            console.warn('Network error in speech synthesis - may retry');
            break;
          case 'synthesis-failed':
            console.warn('Synthesis failed - voice may not be available');
            break;
          case 'synthesis-unavailable':
            console.warn('Synthesis unavailable - browser may have blocked it');
            break;
          case 'audio-busy':
            console.warn('Audio busy - another speech is in progress');
            break;
          case 'not-allowed':
            console.warn('Speech synthesis not allowed - user may need to interact first');
            break;
          default:
            console.warn('Unknown speech synthesis error:', errorType);
        }
        
        setIsSpeaking(false);
        setSubtitles(fullText); // Show full text as subtitles even if speech fails
        isProcessingRef.current = false; // Reset processing flag on error
        
        // On error, don't mark as spoken if it was interrupted (might retry)
        if (errorType !== 'interrupted') {
          // Only remove from spoken set if it was a real error (not interruption)
          spokenTextsRef.current.delete(currentNormalizedText);
        }
        
        if (onSpeakingComplete) {
          onSpeakingComplete();
        }
      };

      utteranceRef.current = utterance;
      
      try {
        window.speechSynthesis.speak(utterance);
      } catch (speakError) {
        console.error('Failed to call speechSynthesis.speak:', speakError);
        isProcessingRef.current = false;
        setIsSpeaking(false);
        setSubtitles(fullText); // Show full text as subtitles
        if (onSpeakingComplete) {
          onSpeakingComplete();
        }
      }
      }, 300); // Increased delay to prevent interruption errors
    }, 200); // 200ms debounce before starting speech
    
    // Cleanup function
    return () => {
      if (textChangeTimeoutRef.current) {
        clearTimeout(textChangeTimeoutRef.current);
      }
      if (speechTimeoutRef.current) {
        clearTimeout(speechTimeoutRef.current);
      }
      isProcessingRef.current = false; // Reset on cleanup
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, [text, autoPlay, onSpeakingComplete, femaleVoice, hasSpokenGreeting]);

  const stopSpeaking = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    if (window.speechSynthesis) {
      // Cancel all speech synthesis
      window.speechSynthesis.cancel();
      // Clear any pending utterances
      if (utteranceRef.current) {
        utteranceRef.current = null;
      }
    }
    setIsSpeaking(false);
    setSubtitles('');
    isProcessingRef.current = false;
    
    // Clear spoken texts when stopped (allow re-speaking)
    if (text) {
      const normalizedText = text.trim().toLowerCase();
      spokenTextsRef.current.delete(normalizedText);
    }
    
    if (onSpeakingComplete) {
      onSpeakingComplete();
    }
  };

  if (!text) return null;

  return (
    <div className="voice-output mt-2">
      {subtitles && (
        <div className="subtitles bg-black/70 text-white p-3 rounded-lg text-sm max-w-md shadow-lg animate-fadeIn">
          <p className="leading-relaxed">{subtitles}</p>
        </div>
      )}
      {isSpeaking && (
        <button
          onClick={stopSpeaking}
          className="stop-button mt-2 text-xs text-gray-500 hover:text-gray-700 underline"
        >
          Stop Speaking
        </button>
      )}
    </div>
  );
}

